import React, { useState } from 'react';
import Button from '../ui/Button';
import Input from '../ui/Input';
import Icon from '../AppIcon';
import emailChangeService from '../../utils/emailChangeService';

/**
 * Reusable Email Change Modal Component
 * Handles email change verification process
 */
const EmailChangeModal = ({ 
  isOpen, 
  onClose, 
  currentEmail, 
  userId,
  onEmailChangeInitiated,
  userRole = 'employer' // 'employer' or 'va'
}) => {
  const [newEmail, setNewEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    // Basic validation
    if (!newEmail || !newEmail.trim()) {
      setError('Please enter a new email address');
      setLoading(false);
      return;
    }

    if (!/^\S+@\S+\.\S+$/.test(newEmail)) {
      setError('Please enter a valid email address');
      setLoading(false);
      return;
    }

    if (newEmail.toLowerCase().trim() === currentEmail.toLowerCase().trim()) {
      setError('New email must be different from current email');
      setLoading(false);
      return;
    }

    try {
      // Determine redirect URL based on user role
      const redirectUrl = userRole === 'employer' 
        ? `${window.location.origin}/auth/verify-email-change?redirect=employer-profile-settings`
        : `${window.location.origin}/auth/verify-email-change?redirect=va-profile-settings`;

      const result = await emailChangeService.initiateEmailChange(
        userId,
        currentEmail,
        newEmail,
        redirectUrl
      );

      if (result.success) {
        setSuccess(result.message || 'Verification email sent successfully!');
        
        // Notify parent component
        if (onEmailChangeInitiated) {
          onEmailChangeInitiated({
            currentEmail,
            newEmail,
            message: result.message
          });
        }

        // Close modal after a delay
        setTimeout(() => {
          onClose();
          setNewEmail('');
          setSuccess('');
        }, 3000);
      } else {
        setError(result.error || 'Failed to send verification email');
      }
    } catch (err) {
      console.error('Error changing email:', err);
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
      setNewEmail('');
      setError('');
      setSuccess('');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-surface rounded-lg border border-border w-full max-w-md mx-auto">
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-text-primary">Change Email Address</h3>
            <button
              onClick={handleClose}
              disabled={loading}
              className="text-text-tertiary hover:text-text-primary transition-colors p-1"
            >
              <Icon name="X" size={20} />
            </button>
          </div>

          {/* Content */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">
                Current Email
              </label>
              <div className="px-3 py-2 bg-secondary-50 border border-border rounded-lg text-text-secondary">
                {currentEmail}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">
                New Email Address *
              </label>
              <Input
                type="email"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                placeholder="Enter new email address"
                disabled={loading}
                error={error && !success}
              />
            </div>

            {/* Instructions */}
            <div className="bg-info-50 border border-info-200 rounded-lg p-4">
              <div className="flex">
                <Icon name="Info" size={16} className="text-info flex-shrink-0 mr-2 mt-0.5" />
                <div className="text-sm text-info">
                  <p className="font-medium mb-1">Email Change Process:</p>
                  <ul className="list-disc list-inside space-y-1 text-xs">
                    <li>A verification email will be sent to your new email address</li>
                    <li>Click the verification link to confirm the change</li>
                    <li>Your email will be updated after verification</li>
                    <li>You may need to sign in again after the change</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Error Message */}
            {error && !success && (
              <div className="bg-error-50 border border-error-200 rounded-lg p-4">
                <div className="flex">
                  <Icon name="AlertCircle" size={16} className="text-error flex-shrink-0 mr-2 mt-0.5" />
                  <p className="text-sm text-error">{error}</p>
                </div>
              </div>
            )}

            {/* Success Message */}
            {success && (
              <div className="bg-success-50 border border-success-200 rounded-lg p-4">
                <div className="flex">
                  <Icon name="CheckCircle" size={16} className="text-success flex-shrink-0 mr-2 mt-0.5" />
                  <div className="text-sm text-success">
                    <p className="font-medium mb-1">Verification Email Sent!</p>
                    <p>{success}</p>
                    <p className="text-xs mt-2">This dialog will close automatically...</p>
                  </div>
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="flex justify-end space-x-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
                disabled={loading}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="primary"
                loading={loading}
                disabled={loading || success}
              >
                {loading ? 'Sending...' : 'Send Verification Email'}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default EmailChangeModal;